import React, { useState, useEffect, useCallback } from 'react';
import { Plus, X, Search, User, Mail, Sparkles } from 'lucide-react';
import {
    IconButton,
    Box,
    Typography,
    Button,
    CircularProgress,
    Paper,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Alert,
    Snackbar,
    InputAdornment,
    Badge,
    Fade,
    Zoom,
    Avatar,
    Tooltip,
    Divider
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupIcon from '@mui/icons-material/Group';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import useFetchWithMsal from "../../../hooks/useFetchWithMsal";
import { protectedResources } from "../../../authConfig";
import { useResponsive } from '../../../hooks/purchaseInvoices/useResponsive.js';
import {
    StyledContainer,
    StyledCard,
    softColors
} from "../../../styles/purchaseInvoices/pages/commonTableStyles.js";

const API_BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;

const GroupManagersTab = () => {
    const { isDesktop } = useResponsive();
    const { execute } = useFetchWithMsal({
        scopes: protectedResources.apiScopes.scopes.read,
    });

    const [groups, setGroups] = useState([]);
    const [groupManagerCounts, setGroupManagerCounts] = useState({});
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [groupManagers, setGroupManagers] = useState({ managers: [] });
    const [newName, setNewName] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingManagers, setIsLoadingManagers] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Fetch groups and manager counts on mount
    useEffect(() => {
        const fetchGroupsAndCounts = async () => {
            setIsLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}/api/groups`);
                const data = await response.json();
                const activeGroups = data.filter(g => g.isActive && !g.isParentGroup);
                setGroups(activeGroups);
                
                // Fetch manager counts for all groups
                const counts = {};
                for (const group of activeGroups) {
                    try {
                        const mgrsResponse = await execute(
                            'GET',
                            `${API_BASE_URL}/api/group-managers/${group._id}`
                        );
                        counts[group._id] = mgrsResponse?.managers?.length || 0;
                    } catch {
                        counts[group._id] = 0;
                    }
                }
                setGroupManagerCounts(counts);
            } catch (err) {
                setError('שגיאה בטעינת הקבוצות');
            }
            setIsLoading(false);
        };
        fetchGroupsAndCounts();
    }, [execute]);

    // Fetch managers when group is selected
    const fetchGroupManagers = useCallback(async (groupId) => {
        if (!groupId) {
            setGroupManagers({ managers: [] });
            return;
        }
        
        setIsLoadingManagers(true);
        try {
            const response = await execute(
                'GET',
                `${API_BASE_URL}/api/group-managers/${groupId}`
            );
            setGroupManagers(response || { managers: [] });
        } catch (err) {
            console.error('Error fetching group managers:', err);
            setGroupManagers({ managers: [] });
        }
        setIsLoadingManagers(false);
    }, [execute]);

    useEffect(() => {
        if (selectedGroupId) {
            fetchGroupManagers(selectedGroupId);
        }
    }, [selectedGroupId, fetchGroupManagers]);

    const handleGroupChange = (event) => {
        setSelectedGroupId(event.target.value);
        setNewName('');
        setNewEmail('');
        setSearchTerm('');
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleAddManager = async () => {
        if (!newName.trim()) {
            setSnackbar({ open: true, message: 'יש להזין שם מנהל', severity: 'warning' });
            return;
        }

        if (!newEmail.trim()) {
            setSnackbar({ open: true, message: 'יש להזין כתובת מייל', severity: 'warning' });
            return;
        }

        if (!validateEmail(newEmail)) {
            setSnackbar({ open: true, message: 'כתובת מייל לא תקינה', severity: 'error' });
            return;
        }

        setIsAdding(true);
        try {
            await execute(
                'POST',
                `${API_BASE_URL}/api/group-managers/${selectedGroupId}`,
                { name: newName.trim(), email: newEmail.trim() }
            );
            setSnackbar({ open: true, message: '✨ המנהל נוסף בהצלחה!', severity: 'success' });
            setNewName('');
            setNewEmail('');
            fetchGroupManagers(selectedGroupId);
            // Update count
            setGroupManagerCounts(prev => ({
                ...prev,
                [selectedGroupId]: (prev[selectedGroupId] || 0) + 1
            }));
        } catch (err) {
            const errorMessage = err.message || 'שגיאה בהוספת המנהל';
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        }
        setIsAdding(false);
    };

    const handleRemoveManager = async (email) => {
        try {
            await execute(
                'DELETE',
                `${API_BASE_URL}/api/group-managers/${selectedGroupId}/${encodeURIComponent(email)}`
            );
            setSnackbar({ open: true, message: 'המנהל הוסר בהצלחה', severity: 'success' });
            fetchGroupManagers(selectedGroupId);
            // Update count
            setGroupManagerCounts(prev => ({
                ...prev,
                [selectedGroupId]: Math.max((prev[selectedGroupId] || 1) - 1, 0)
            }));
        } catch (err) {
            setSnackbar({ open: true, message: 'שגיאה בהסרת המנהל', severity: 'error' });
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleAddManager();
        }
    };

    const selectedGroup = groups.find(g => g._id === selectedGroupId);
    
    // Filter managers by search term
    const filteredManagers = groupManagers.managers?.filter(manager => 
        !searchTerm || 
        manager.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        manager.email.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    // Get initials for avatar
    const getInitials = (name) => {
        return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
    };

    // Get color based on email hash
    const getAvatarColor = (email) => {
        const colors = ['#1976d2', '#388e3c', '#d32f2f', '#7b1fa2', '#f57c00', '#0097a7', '#5d4037', '#455a64'];
        const hash = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        return colors[hash % colors.length];
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {error}
            </Alert>
        );
    }

    return (
        <StyledContainer>
            <StyledCard>
                <Box sx={{ p: { xs: 2, md: 3 } }}>
                    {/* Header with gradient */}
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2, 
                        mb: 3,
                        pb: 2,
                        borderBottom: '2px solid',
                        borderImage: 'linear-gradient(90deg, #1976d2, #42a5f5, transparent) 1'
                    }}>
                        <Box sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                        }}>
                            <GroupIcon sx={{ fontSize: 28, color: 'white' }} />
                        </Box>
                        <Box>
                            <Typography variant="h5" fontWeight="bold" sx={{ 
                                background: 'linear-gradient(90deg, #1976d2, #1565c0)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                            }}>
                                מנהלי קבוצות
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                ניהול רשימת המנהלים לכל קבוצה • שליחת בירורים
                            </Typography>
                        </Box>
                    </Box>

                    {/* Group Selection with Badge */}
                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel id="group-select-label">בחר קבוצה</InputLabel>
                        <Select
                            labelId="group-select-label"
                            value={selectedGroupId}
                            label="בחר קבוצה"
                            onChange={handleGroupChange}
                            sx={{ 
                                backgroundColor: 'white',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderWidth: 2,
                                    transition: 'border-color 0.3s'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#1976d2'
                                }
                            }}
                        >
                            {groups.map((group) => (
                                <MenuItem key={group._id} value={group._id}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                        <span>{group.name}</span>
                                        <Chip 
                                            size="small" 
                                            label={`${groupManagerCounts[group._id] || 0} מנהלים`}
                                            sx={{ 
                                                ml: 1,
                                                backgroundColor: groupManagerCounts[group._id] > 0 ? '#e3f2fd' : '#f5f5f5',
                                                color: groupManagerCounts[group._id] > 0 ? '#1976d2' : '#9e9e9e',
                                                fontWeight: 'bold',
                                                fontSize: '0.7rem'
                                            }}
                                        />
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {selectedGroupId && (
                        <Fade in={true} timeout={300}>
                            <Box>
                                {/* Add Manager Section */}
                                <Paper 
                                    elevation={0} 
                                    sx={{ 
                                        p: 3, 
                                        mb: 3, 
                                        background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                                        border: '1px solid #90caf9',
                                        borderRadius: 3,
                                        position: 'relative',
                                        overflow: 'hidden',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            height: 4,
                                            background: 'linear-gradient(90deg, #1976d2, #42a5f5)'
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <PersonAddIcon sx={{ color: '#1976d2' }} />
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1565c0' }}>
                                            הוסף מנהל חדש
                                        </Typography>
                                    </Box>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        gap: 2, 
                                        flexDirection: isDesktop ? 'row' : 'column',
                                        alignItems: isDesktop ? 'flex-start' : 'stretch'
                                    }}>
                                        <TextField
                                            size="small"
                                            label="שם מנהל"
                                            placeholder="הזן שם מלא"
                                            value={newName}
                                            onChange={(e) => setNewName(e.target.value)}
                                            sx={{ 
                                                backgroundColor: 'white', 
                                                flex: 1,
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2
                                                }
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <User size={18} color="#1976d2" />
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                        <TextField
                                            size="small"
                                            label="כתובת מייל"
                                            placeholder="הזן כתובת מייל"
                                            value={newEmail}
                                            onChange={(e) => setNewEmail(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            type="email"
                                            sx={{ 
                                                backgroundColor: 'white', 
                                                flex: 1,
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2
                                                }
                                            }}
                                            InputProps={{
                                                dir: 'ltr',
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Mail size={18} color="#1976d2" />
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleAddManager}
                                            disabled={isAdding}
                                            startIcon={isAdding ? <CircularProgress size={18} color="inherit" /> : <PersonAddIcon />}
                                            sx={{ 
                                                minWidth: isDesktop ? 140 : '100%',
                                                whiteSpace: 'nowrap',
                                                borderRadius: 2,
                                                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                                                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                                                transition: 'all 0.3s',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)'
                                                }
                                            }}
                                        >
                                            {isAdding ? 'מוסיף...' : 'הוסף מנהל'}
                                        </Button>
                                    </Box>
                                </Paper>

                                {/* Managers List */}
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                מנהלים בקבוצה "{selectedGroup?.name}"
                                            </Typography>
                                            <Chip 
                                                size="small" 
                                                label={groupManagers.managers?.length || 0}
                                                color="primary"
                                                sx={{ fontWeight: 'bold' }}
                                            />
                                        </Box>
                                        
                                        {groupManagers.managers?.length > 3 && (
                                            <TextField
                                                size="small"
                                                placeholder="חיפוש מנהל..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                sx={{ 
                                                    width: 200,
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        backgroundColor: 'white'
                                                    }
                                                }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Search size={16} />
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        )}
                                    </Box>

                                    {isLoadingManagers ? (
                                        <Box display="flex" justifyContent="center" p={4}>
                                            <CircularProgress size={32} />
                                        </Box>
                                    ) : filteredManagers.length > 0 ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            {filteredManagers.map((manager, index) => (
                                                <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }} key={manager.email}>
                                                    <Paper
                                                        elevation={0}
                                                        sx={{
                                                            p: 2,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 2,
                                                            backgroundColor: 'white',
                                                            border: '1px solid #e0e0e0',
                                                            borderRadius: 2,
                                                            transition: 'all 0.3s',
                                                            '&:hover': {
                                                                borderColor: '#1976d2',
                                                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                                                transform: 'translateX(-4px)'
                                                            }
                                                        }}
                                                    >
                                                        <Avatar sx={{ 
                                                            bgcolor: getAvatarColor(manager.email),
                                                            width: 44,
                                                            height: 44,
                                                            fontWeight: 'bold',
                                                            fontSize: '0.9rem'
                                                        }}>
                                                            {getInitials(manager.name)}
                                                        </Avatar>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="subtitle2" fontWeight="bold">
                                                                {manager.name}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" dir="ltr" sx={{ textAlign: 'right' }}>
                                                                {manager.email}
                                                            </Typography>
                                                        </Box>
                                                        <Tooltip title="הסר מנהל">
                                                            <IconButton 
                                                                size="small"
                                                                onClick={() => handleRemoveManager(manager.email)}
                                                                sx={{ 
                                                                    color: '#9e9e9e',
                                                                    transition: 'all 0.2s',
                                                                    '&:hover': { 
                                                                        backgroundColor: '#ffebee',
                                                                        color: '#d32f2f',
                                                                        transform: 'scale(1.1)'
                                                                    }
                                                                }}
                                                            >
                                                                <X size={18} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Paper>
                                                </Zoom>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Paper 
                                            elevation={0}
                                            sx={{ 
                                                p: 4, 
                                                textAlign: 'center',
                                                background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
                                                borderRadius: 3,
                                                border: '2px dashed #e0e0e0'
                                            }}
                                        >
                                            <Box sx={{
                                                width: 64,
                                                height: 64,
                                                borderRadius: '50%',
                                                backgroundColor: '#f5f5f5',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                margin: '0 auto 16px'
                                            }}>
                                                <GroupIcon sx={{ fontSize: 32, color: '#bdbdbd' }} />
                                            </Box>
                                            <Typography color="text.secondary" fontWeight="medium">
                                                {searchTerm ? 'לא נמצאו תוצאות' : 'אין מנהלים מוגדרים לקבוצה זו'}
                                            </Typography>
                                            {!searchTerm && (
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                    הוסף מנהלים באמצעות הטופס למעלה ☝️
                                                </Typography>
                                            )}
                                        </Paper>
                                    )}
                                </Box>
                            </Box>
                        </Fade>
                    )}

                    {!selectedGroupId && (
                        <Paper 
                            elevation={0}
                            sx={{ 
                                p: 5, 
                                textAlign: 'center',
                                background: 'linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%)',
                                borderRadius: 3,
                                border: '2px dashed #e0e0e0'
                            }}
                        >
                            <Box sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 20px',
                                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)'
                            }}>
                                <GroupIcon sx={{ fontSize: 40, color: '#1976d2' }} />
                            </Box>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                בחר קבוצה להתחיל
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                בחר קבוצה מהרשימה למעלה כדי לצפות ולערוך את המנהלים שלה
                            </Typography>
                        </Paper>
                    )}
                </Box>
            </StyledCard>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setSnackbar({ ...snackbar, open: false })} 
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </StyledContainer>
    );
};

export default GroupManagersTab;
